import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const unique = (arr) => Array.from(new Set(arr));
const close = (a, b) => Math.abs(a - b) < 0.001;
const countOptions = [0, 1, 2, 3, 4].map((value) => ({
  value: String(value),
  label: value === 0 ? "brak rozwiązań" : value === 1 ? "1 rozwiązanie" : `${value} rozwiązania`,
}));

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

function formatNumberLatex(value) {
  if (Number.isInteger(value)) return String(value);
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  for (let den = 2; den <= 12; den += 1) {
    const num = Math.round(abs * den);
    if (close(num / den, abs)) {
      const g = gcd(num, den);
      return sign + `\\frac{${num / g}}{${den / g}}`;
    }
  }
  return String(Number(value.toFixed(3)));
}

function solutionSetLatex(roots = []) {
  return roots.length ? `\\{${roots.map(formatNumberLatex).join(",")}\\}` : "\\varnothing";
}

function linearFactorLatex({ a = 1, b = 0 }) {
  const xPart = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  return b === 0 ? xPart : `${xPart}${b > 0 ? "+" : ""}${b}`;
}

function quadraticFactorLatex({ b = 0, c = 0 }) {
  const bx = b === 0 ? "" : b === 1 ? "+x" : b === -1 ? "-x" : `${b > 0 ? "+" : ""}${b}x`;
  const constant = c === 0 ? "" : `${c > 0 ? "+" : ""}${c}`;
  return `x^2${bx}${constant}`;
}

function factorLatex(factor) {
  if (factor.kind === "linear") return linearFactorLatex(factor);
  if (factor.kind === "quadratic") return quadraticFactorLatex(factor);
  return factor.factorLatex || factor.latex;
}

function solveLinear({ a = 1, b = 0 }) {
  return -b / a;
}

function rootsFromFactor(factor) {
  if (factor.kind === "linear") return [solveLinear(factor)];
  return factor.roots || [];
}

function quadraticDelta({ b = 0, c = 0 }) {
  return b * b - 4 * c;
}
function quadraticRootFormulaLines(factor, roots) {
  const delta = quadraticDelta(factor);
  const sqrtDelta = Math.sqrt(delta);
  if (roots.length === 1) {
    return [
      <BlockMath key="square" math={`${factorLatex(factor)}=(x-${formatNumberLatex(roots[0])})^2`} />,
      <BlockMath key="root" math={`(x-${formatNumberLatex(roots[0])})^2=0\\quad\\Rightarrow\\quad x=${formatNumberLatex(roots[0])}`} />,
    ];
  }
  if (roots.length > 1) {
    return [
      <div key="roots" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, alignItems: "start" }}>
        <BlockMath math={`x_1=\\frac{-(${factor.b})-${formatNumberLatex(sqrtDelta)}}{2\\cdot1}=${formatNumberLatex(roots[0])}`} />
        <BlockMath math={`x_2=\\frac{-(${factor.b})+${formatNumberLatex(sqrtDelta)}}{2\\cdot1}=${formatNumberLatex(roots[1])}`} />
      </div>,
    ];
  }
  return [<p key="none">Delta jest ujemna, więc ten czynnik nie daje rozwiązań rzeczywistych.</p>];
}

function buildTask({ variant, factors, note, noteLatex = null, expandedLatex = null, formulaLatex = null }) {
  const roots = unique(factors.flatMap(rootsFromFactor)).sort((a, b) => a - b);
  const factorLatexText = factors.map((factor) => `(${factorLatex(factor)})`).join("");
  const equationLatex = `${expandedLatex || factorLatexText}=0`;
  return {
    type: "ProductFactorsPolynomialEquationTask",
    data: { variant, factors, note, noteLatex, roots, equationLatex, factorLatex: factorLatexText, formulaLatex },
    tags: ["algebra", "rownania", "rownania-wielomianowe", variant],
  };
}

function randomRoots(count) {
  const pool = [-8, -7, -6, -5, -4, -3, -2, 1, 2, 3, 4, 5, 6, 7, 8];
  const result = [];
  while (result.length < count) {
    const root = pick(pool);
    if (!result.includes(root)) result.push(root);
  }
  return result;
}

function generateSimpleLinearTask(factorCount = rand(2, 4)) {
  const roots = randomRoots(factorCount);
  return buildTask({
    variant: "simpleLinear",
    factors: roots.map((root) => ({ kind: "linear", a: 1, b: -root })),
    note: "Każdy nawias ma postać x-a, więc rozwiązania odczytujemy prawie od razu.",
  });
}

function generateLinearCoefficientsTask(factorCount = rand(2, 4)) {
  const roots = randomRoots(factorCount);
  const coefficients = [2, 3, -2, -3];
  const factors = roots.map((root, index) => ({ kind: "linear", a: coefficients[index], b: -coefficients[index] * root }));
  if (factors.length > 2) {
    factors[0] = { kind: "linear", a: 1, b: -roots[0] };
  }
  return buildTask({
    variant: "linearCoefficients",
    factors,
    note: "W czynnikach ax+b rozwiązujemy krótkie równania liniowe. Gdy nawias ma postać x-a, pierwiastek odczytujemy od razu.",
  });
}

function quadraticBlockFactors(mode = pick(["irreducible", "square", "difference", "regularDelta"])) {
  if (mode === "irreducible") return [{ kind: "quadratic", b: 0, c: pick([4, 9, 16]), roots: [] }];
  if (mode === "square") {
    const p = pick([-5, -3, 2, 4]);
    return [{ kind: "quadratic", b: -2 * p, c: p * p, roots: [p] }];
  }
  if (mode === "regularDelta") {
    const r1 = pick([-5, -4, -2, 2, 3]);
    const r2 = pick([4, 5, 6, 7].filter((value) => value !== r1));
    return [{ kind: "quadratic", b: -(r1 + r2), c: r1 * r2, roots: [r1, r2].sort((a, b) => a - b), solveHint: "delta" }];
  }
  const p = pick([2, 3, 4, 5]);
  return [{ kind: "quadratic", b: 0, c: -p * p, roots: [-p, p] }];
}

function generateQuadraticBlockSolveTask({ outsideRootCount = rand(1, 2), quadraticMode } = {}) {
  const quadraticFactor = quadraticBlockFactors(quadraticMode)[0];
  const excludedRoots = new Set(quadraticFactor.roots || []);
  const outsideRoots = randomRoots(outsideRootCount).filter((root) => !excludedRoots.has(root));
  while (outsideRoots.length < outsideRootCount) {
    const candidate = randomRoots(1)[0];
    if (!excludedRoots.has(candidate) && !outsideRoots.includes(candidate)) outsideRoots.push(candidate);
  }
  return buildTask({
    variant: "quadraticBlockSolve",
    factors: [
      ...outsideRoots.map((root) => ({ kind: "linear", a: 1, b: -root })),
      quadraticFactor,
    ],
    note: "Przy czynniku kwadratowym sprawdzamy, czy jest nierozkładalny, jest kwadratem dwumianu, różnicą kwadratów albo zwykłym trójmianem do rozwiązania z delty.",
  });
}

function generateRepeatedRootTask() {
  const repeatedRoot = pick([-4, -2, 2, 3, 5]);
  const extraRoot = pick(randomRoots(6).filter((root) => root !== repeatedRoot));
  return buildTask({
    variant: "repeatedRoot",
    factors: [
      { kind: "linear", a: 1, b: -extraRoot },
      { kind: "quadratic", b: -2 * repeatedRoot, c: repeatedRoot * repeatedRoot, roots: [repeatedRoot] },
    ],
    note: "Ten sam pierwiastek może pojawić się kilka razy, ale w zbiorze rozwiązań zapisujemy go tylko raz.",
  });
}

function generateCubicIdentityTask() {
  const p = pick([2, 3, 4, 5]);
  const sign = pick(["minus", "plus"]);
  const root = sign === "minus" ? p : -p;
  const cubicLatex = sign === "minus" ? `x^3-${p ** 3}` : `x^3+${p ** 3}`;
  const formulaLatex = sign === "minus"
    ? `x^3-${p ** 3}=(x-${p})(x^2+${p}x+${p * p})`
    : `x^3+${p ** 3}=(x+${p})(x^2-${p}x+${p * p})`;
  const extraRoot = pick(randomRoots(6).filter((candidate) => candidate !== root));
  return buildTask({
    variant: "cubicIdentity",
    factors: [
      { kind: "linear", a: 1, b: -extraRoot },
      { kind: "raw", latex: cubicLatex, factorLatex: cubicLatex, roots: [root] },
    ],
    formulaLatex,
    note: sign === "minus" ? "Korzystamy ze wzoru na różnicę sześcianów." : "Korzystamy ze wzoru na sumę sześcianów.",
  });
}

function generateGroupingTask() {
  const p = pick([2, 3, 4]);
  const q = pick([2, 3, 5]);
  return buildTask({
    variant: "grouping",
    factors: [
      { kind: "linear", a: 1, b: p },
      { kind: "quadratic", b: 0, c: -q * q, roots: [-q, q] },
    ],
    expandedLatex: `x^3+${p}x^2-${q * q}x-${p * q * q}`,
    noteLatex: `x^2(x+${p})-${q * q}(x+${p})`,
    note: "Grupujemy wyrazy parami i wyłączamy wspólny nawias:",
  });
}

export function generateBiquadraticCountData() {
  const mode = pick(["zero", "two", "four"]);
  if (mode === "zero") {
    const a = pick([2, 3, 5]);
    const b = pick([4, 6, 8]);
    const delta = a * a - 4 * b;
    const tRoots = delta >= 0 ? [(-a - Math.sqrt(delta)) / 2, (-a + Math.sqrt(delta)) / 2] : [];
    return { variant: "biquadraticCount", equationLatex: `x^4+${a}x^2+${b}=0`, roots: [], tEquationLatex: `t^2+${a}t+${b}=0`, tCoefficients: { a: 1, b: a, c: b }, tRoots, note: "Po podstawieniu nie otrzymujemy dodatniego rozwiązania dla t." };
  }
  if (mode === "two") {
    const r = pick([2, 3, 4]);
    const s = pick([2, 5, 10]);
    const coefficient = s - r * r;
    const middle = coefficient === 0 ? "" : coefficient > 0 ? `+${coefficient}x^2` : `${coefficient}x^2`;
    const tMiddle = coefficient === 0 ? "" : coefficient > 0 ? `+${coefficient}t` : `${coefficient}t`;
    return { variant: "biquadraticCount", equationLatex: `x^4${middle}-${r * r * s}=0`, roots: [-r, r], tEquationLatex: `t^2${tMiddle}-${r * r * s}=0`, tCoefficients: { a: 1, b: coefficient, c: -r * r * s }, tRoots: [-s, r * r], note: "Po podstawieniu dostajemy jedno dodatnie i jedno ujemne rozwiązanie t." };
  }
  const p = pick([2, 3]);
  const q = pick([4, 5]);
  return { variant: "biquadraticCount", equationLatex: `x^4-${p * p + q * q}x^2+${p * p * q * q}=0`, roots: [-q, -p, p, q], tEquationLatex: `t^2-${p * p + q * q}t+${p * p * q * q}=0`, tCoefficients: { a: 1, b: -(p * p + q * q), c: p * p * q * q }, tRoots: [p * p, q * q], note: "Po podstawieniu dostajemy dwa dodatnie rozwiązania t." };
}

export function generateTask(generationOptions = "simpleLinear") {
  const options = typeof generationOptions === "string"
    ? { variant: generationOptions }
    : (generationOptions || {});
  const variant = options.variant || "simpleLinear";

  if (variant === "simpleLinear") return generateSimpleLinearTask(options.factorCount);
  if (variant === "linearCoefficients") return generateLinearCoefficientsTask(options.factorCount);
  if (variant === "quadraticBlockSolve") return generateQuadraticBlockSolveTask(options);
  if (variant === "repeatedRoot") return generateRepeatedRootTask();
  if (variant === "cubicIdentity") return generateCubicIdentityTask();
  if (variant === "grouping") return generateGroupingTask();
  return generateSimpleLinearTask(options.factorCount);
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>Rozwiąż równanie wielomianowe w zbiorze liczb rzeczywistych.</p>
      <BlockMath math={data.equationLatex} />
      <p>Określ liczbę rozwiązań i wpisz wszystkie rozwiązania.</p>
    </div>
  );
}

export function renderInput(_, userAnswer = {}, onChange, disabled) {
  const count = userAnswer.count ?? "";
  const inputCount = Number.isInteger(Number(count)) && Number(count) > 0 ? Number(count) : 0;
  return (
    <div style={{ display: "grid", gap: 10, maxWidth: 560 }}>
      <div style={{ maxWidth: 360 }}>
        <SelectInput options={countOptions} value={count} onChange={(e) => onChange({ ...(userAnswer || {}), count: e?.target?.value ?? e })} placeholder="Wybierz liczbę rozwiązań" disabled={disabled} />
      </div>
      {Array.from({ length: inputCount }, (_, index) => {
        const key = `x${index + 1}`;
        return (
          <div key={key} style={{ display: "grid", gap: 6, maxWidth: 520 }}>
            <span style={{ fontWeight: 700 }}><InlineMath math={`x_${index + 1} =`} /></span>
            <FractionInput value={userAnswer[key] || ""} onChange={(value) => onChange({ ...(userAnswer || {}), [key]: value })} disabled={disabled} label={`x_${index + 1}`} />
          </div>
        );
      })}
    </div>
  );
}

function userRoots(userAnswer = {}) {
  const count = Number(userAnswer.count);
  if (!Number.isInteger(count) || count <= 0) return [];
  return Array.from({ length: count }, (_, index) => userAnswer[`x${index + 1}`]?.parsed).filter((value) => typeof value === "number" && Number.isFinite(value)).sort((a, b) => a - b);
}

export function checkAnswer({ data }, userAnswer = {}) {
  if (String(userAnswer.count) !== String(data.roots.length)) return false;
  const actual = userRoots(userAnswer);
  return actual.length === data.roots.length && data.roots.every((root, index) => close(root, actual[index]));
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <div><strong>Liczba rozwiązań:</strong> <InlineMath math={String(data.roots.length)} /></div>
      <div style={{ marginTop: 6 }}><strong>Rozwiązania:</strong> {data.roots.length > 0 ? <InlineMath math={`x\\in${solutionSetLatex(data.roots)}`} /> : <span>brak</span>}</div>
    </div>
  );
}

function renderFactorCheck(factor, index) {
  if (factor.kind === "linear") {
    const root = solveLinear(factor);
    return (
      <div key={index} style={{ marginTop: 10 }}>
        <BlockMath math={`${factorLatex(factor)}=0\\quad\\Rightarrow\\quad x=${formatNumberLatex(root)}`} />
      </div>
    );
  }

  if (factor.kind === "quadratic") {
    const roots = rootsFromFactor(factor);
    const delta = quadraticDelta(factor);
    const isSquare = roots.length === 1;
    const isDifference = factor.b === 0 && factor.c < 0;
    const isPositiveSquareSum = factor.b === 0 && factor.c > 0;

    if (isPositiveSquareSum) {
      return (
        <div key={index} style={{ marginTop: 10 }}>
          <BlockMath math={`${factorLatex(factor)}=0\\quad\\Rightarrow\\quad x^2=${-factor.c}`} />
          <p>Kwadrat liczby rzeczywistej nie może być ujemny, więc ten czynnik nie daje rozwiązań rzeczywistych.</p>
        </div>
      );
    }

    if (isDifference) {
      const q = Math.sqrt(-factor.c);
      return (
        <div key={index} style={{ marginTop: 10 }}>
          <p>Ten czynnik rozpoznajemy jako różnicę kwadratów.</p>
          <BlockMath math={`x^2-${q * q}=0\\quad\\Longleftrightarrow\\quad (x-${q})(x+${q})=0`} />
          <BlockMath math={`x=${formatNumberLatex(-q)}\\quad\\text{lub}\\quad x=${formatNumberLatex(q)}`} />
        </div>
      );
    }

    return (
      <div key={index} style={{ marginTop: 10 }}>
        <p>{isSquare ? "Ten trójmian jest wzorem skróconego mnożenia: kwadratem dwumianu. Dlatego ma jeden pierwiastek podwójny." : "Ten trójmian rozwiązujemy jak zwykłe równanie kwadratowe."}</p>
        <BlockMath math={`\\Delta=${factor.b}^{2}-4\\cdot1\\cdot(${factor.c})=${delta}`} />
        {quadraticRootFormulaLines(factor, roots)}
      </div>
    );
  }

  const roots = rootsFromFactor(factor);
  let comment = "\\text{brak rozwiązań rzeczywistych}";
  if (roots.length === 1) comment = `x=${formatNumberLatex(roots[0])}`;
  if (roots.length > 1) comment = `x\\in${solutionSetLatex(roots)}`;
  return (
    <div key={index} style={{ marginTop: 10 }}>
      <BlockMath math={`${factorLatex(factor)}=0\\quad\\Rightarrow\\quad ${comment}`} />
    </div>
  );
}

function renderGroupingExpansion(data) {
  if (data.variant !== "grouping") return null;
  const p = data.factors?.[0]?.b || 0;
  const q = Math.sqrt(-(data.factors?.[1]?.c || 0));
  return (
    <>
      <p>Grupujemy wyrazy parami i z każdej grupy wyłączamy wspólny czynnik.</p>
      <BlockMath math={`${data.equationLatex.replace(/=0$/, "")} = x^2(x+${p})-${q * q}(x+${p})`} />
      <p>W obu grupach powstał ten sam nawias, więc wyłączamy go przed nawias.</p>
      <BlockMath math={`x^2(x+${p})-${q * q}(x+${p})=(x+${p})(x^2-${q * q})`} />
      <p>Drugi nawias jest różnicą kwadratów.</p>
      <BlockMath math={`(x+${p})(x^2-${q * q})=(x+${p})(x-${q})(x+${q})`} />
    </>
  );
}

export function renderExplanation({ data }) {
  const equationBody = data.equationLatex.replace(/=0$/, "");
  const hasRealFactorization = data.variant !== "grouping" && data.factorLatex && data.factorLatex !== equationBody;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Zapisujemy równanie jako iloczyn czynników.</p>
      <BlockMath math={data.equationLatex} />
      {renderGroupingExpansion(data)}
      {data.formulaLatex && <BlockMath math={data.formulaLatex} />}
      {hasRealFactorization && <BlockMath math={`${data.factorLatex}=0`} />}
      {data.variant !== "grouping" && data.note && <p><strong>Uwaga.</strong> {data.note}{data.noteLatex && <> <InlineMath math={data.noteLatex} />.</>}</p>}
      <p><strong>Krok 2.</strong> Sprawdzamy każdy czynnik osobno.</p>
      {data.factors.map(renderFactorCheck)}
      <p><strong>Liczba rozwiązań:</strong> <InlineMath math={String(data.roots.length)} />.</p>
      <p><strong>Odpowiedź:</strong> <InlineMath math={data.roots.length ? `x\\in${solutionSetLatex(data.roots)}` : "\\text{brak rozwiązań}"} />.</p>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const count = Number(userAnswer.count);
  if (!Number.isInteger(count)) return <i>(brak odpowiedzi)</i>;
  const parts = Array.from({ length: count > 0 ? count : 0 }, (_, index) => {
    const key = `x${index + 1}`;
    const raw = userAnswer?.[key]?.raw;
    return raw ? formatLatexPreview(raw, `x_${index + 1}`) : `x_${index + 1}=\\text{brak}`;
  });
  return (
    <div style={{ textAlign: "left" }}>
      <div><strong>Liczba rozwiązań:</strong> <InlineMath math={String(count)} /></div>
      {parts.length > 0 && <div style={{ marginTop: 6 }}><InlineMath math={parts.join(",\\quad ")} /></div>}
    </div>
  );
}
